import { createClient, SupabaseClient } from '@supabase/supabase-js';

const STORAGE_SUPABASE_URL = 'mylife_supabase_url';
const STORAGE_SUPABASE_KEY = 'mylife_supabase_key';

export const getStoredSupabaseConfig = () => {
  const envUrl = (import.meta as any).env?.VITE_SUPABASE_URL || '';
  const envKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || '';

  const localUrl = (localStorage.getItem(STORAGE_SUPABASE_URL) || envUrl).trim();
  const localKey = (localStorage.getItem(STORAGE_SUPABASE_KEY) || envKey).trim();

  // Validate that credentials look like valid Supabase project credentials
  const isRealUrl = localUrl.startsWith('https://') && localUrl.includes('.supabase.co');
  const isRealKey = localKey.length > 40 && !localKey.includes('example_key') && !localKey.includes('your-supabase');

  const isConfigured = Boolean(isRealUrl && isRealKey);

  return {
    url: localUrl,
    key: localKey,
    isConfigured,
  };
};

let cachedClient: SupabaseClient | null = null;
let cachedConfigKey = '';

export const saveSupabaseConfig = (url: string, key: string) => {
  localStorage.setItem(STORAGE_SUPABASE_URL, url.trim());
  localStorage.setItem(STORAGE_SUPABASE_KEY, key.trim());
  cachedClient = null;
  cachedConfigKey = '';
};

export const clearSupabaseConfig = () => {
  localStorage.removeItem(STORAGE_SUPABASE_URL);
  localStorage.removeItem(STORAGE_SUPABASE_KEY);
  cachedClient = null;
  cachedConfigKey = '';
};

export const getSupabaseClient = (): SupabaseClient | null => {
  const config = getStoredSupabaseConfig();
  if (!config.isConfigured) return null;

  const currentKey = `${config.url}_${config.key}`;
  if (cachedClient && cachedConfigKey === currentKey) {
    return cachedClient;
  }

  try {
    cachedClient = createClient(config.url, config.key, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
    });
    cachedConfigKey = currentKey;
    return cachedClient;
  } catch (err) {
    console.warn('Failed to initialize Supabase client:', err);
    return null;
  }
};

export const testSupabaseConnection = async (): Promise<{ success: boolean; error?: string }> => {
  const client = getSupabaseClient();
  if (!client) {
    return { success: false, error: 'Supabase URL or Key is not configured' };
  }
  try {
    const { error } = await client.from('mylife_sync').select('id').limit(1);
    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message || 'Connection failed' };
  }
};
